/**
 * System Settings Component
 * Comprehensive system configuration and settings management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Avatar,
  LinearProgress,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  Key as KeyIcon,
  Dataset as DatabaseIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Backup as BackupIcon,
  Schedule as ScheduleIcon,
  MonitorHeart as MonitorIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';

interface SystemSettings {
  general: {
    siteName: string;
    siteUrl: string;
    adminEmail: string;
    timezone: string;
    dateFormat: string;
    maintenanceMode: boolean;
    debugMode: boolean;
  };
  security: {
    sessionTimeout: number;
    maxLoginAttempts: number;
    passwordMinLength: number;
    requireMFA: boolean;
    allowedIPs: string[];
    sslEnforced: boolean;
  };
  aws: {
    region: string;
    accessKeyId: string;
    bucketName: string;
    cloudWatchEnabled: boolean;
    costAlertsEnabled: boolean;
    backupRetentionDays: number;
  };
  notifications: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    slackEnabled: boolean;
    webhookUrl: string;
    alertThresholds: {
      errorRate: number;
      responseTime: number;
      diskUsage: number;
      memoryUsage: number;
    };
  };
  performance: {
    cacheEnabled: boolean;
    cacheTTL: number;
    rateLimitEnabled: boolean;
    rateLimitWindow: number;
    compressionEnabled: boolean;
    cdnEnabled: boolean;
  };
  backup: {
    autoBackupEnabled: boolean;
    backupFrequency: string;
    retentionPeriod: number;
    s3BackupEnabled: boolean;
    encryptionEnabled: boolean;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const Settings: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [newKeyDialog, setNewKeyDialog] = useState(false);
  const [testConnectionDialog, setTestConnectionDialog] = useState(false);
  const [systemHealth, setSystemHealth] = useState<any>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      // Mock settings for now - replace with actual API call
      const mockSettings: SystemSettings = {
        general: {
          siteName: 'LifeScribe Admin Dashboard',
          siteUrl: 'https://admin.lifescribe.com',
          adminEmail: 'admin@lifescribe.com',
          timezone: 'America/New_York',
          dateFormat: 'MM/DD/YYYY',
          maintenanceMode: false,
          debugMode: false,
        },
        security: {
          sessionTimeout: 30,
          maxLoginAttempts: 5,
          passwordMinLength: 8,
          requireMFA: true,
          allowedIPs: ['192.168.1.0/24', '10.0.0.0/8'],
          sslEnforced: true,
        },
        aws: {
          region: 'us-east-1',
          accessKeyId: 'AKIA****',
          bucketName: 'lifescribe-admin-data',
          cloudWatchEnabled: true,
          costAlertsEnabled: true,
          backupRetentionDays: 30,
        },
        notifications: {
          emailEnabled: true,
          smsEnabled: true,
          slackEnabled: false,
          webhookUrl: '',
          alertThresholds: {
            errorRate: 5.0,
            responseTime: 2000,
            diskUsage: 85,
            memoryUsage: 90,
          },
        },
        performance: {
          cacheEnabled: true,
          cacheTTL: 3600,
          rateLimitEnabled: true,
          rateLimitWindow: 60,
          compressionEnabled: true,
          cdnEnabled: true,
        },
        backup: {
          autoBackupEnabled: true,
          backupFrequency: 'daily',
          retentionPeriod: 30,
          s3BackupEnabled: true,
          encryptionEnabled: true,
        },
      };
      setSettings(mockSettings);
      
      // Load system health
      const healthData = {
        status: 'operational',
        uptime: 99.8,
        lastBackup: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
        diskUsage: 67,
        memoryUsage: 72,
        services: {
          database: 'healthy',
          redis: 'healthy',
          aws: 'healthy',
          email: 'healthy',
        },
      };
      setSystemHealth(healthData);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load system settings',
      }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      // Mock save - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      dispatch(addNotification({
        type: 'success',
        message: 'Settings saved successfully',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to save settings',
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = () => {
    setTestConnectionDialog(true);
    // Mock test - replace with actual API call
    setTimeout(() => {
      setTestConnectionDialog(false);
      dispatch(addNotification({
        type: 'success',
        message: 'AWS connection test successful',
      }));
    }, 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <MonitorIcon />;
    }
  };

  if (loading || !settings) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading system settings...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            System Settings
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <IconButton onClick={loadSettings} color="primary">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Configure system-wide settings, security, integrations, and monitoring
        </Typography>
      </Box>

      {/* System Health Overview */}
      {systemHealth && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              System Health Status
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: getStatusColor(systemHealth.status) }}>
                    {getStatusIcon(systemHealth.status)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {systemHealth.status}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Uptime: {systemHealth.uptime}%
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Last Backup: {new Date(systemHealth.lastBackup).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Disk Usage: {systemHealth.diskUsage}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={systemHealth.diskUsage} 
                    sx={{ height: 6, borderRadius: 1 }}
                    color={systemHealth.diskUsage > 80 ? 'warning' : 'primary'}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Memory Usage: {systemHealth.memoryUsage}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={systemHealth.memoryUsage} 
                    sx={{ height: 6, borderRadius: 1 }}
                    color={systemHealth.memoryUsage > 85 ? 'warning' : 'primary'}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Settings Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="General" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="Security" icon={<SecurityIcon />} iconPosition="start" />
            <Tab label="AWS Integration" icon={<CloudIcon />} iconPosition="start" />
            <Tab label="Notifications" icon={<NotificationsIcon />} iconPosition="start" />
            <Tab label="Performance" icon={<SpeedIcon />} iconPosition="start" />
            <Tab label="Backup" icon={<BackupIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* General Settings */}
        <TabPanel value={activeTab} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Site Name"
                  value={settings.general.siteName}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    general: { ...prev.general, siteName: e.target.value }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Site URL"
                  value={settings.general.siteUrl}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    general: { ...prev.general, siteUrl: e.target.value }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Admin Email"
                  type="email"
                  value={settings.general.adminEmail}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    general: { ...prev.general, adminEmail: e.target.value }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    value={settings.general.timezone}
                    label="Timezone"
                    onChange={(e) => setSettings(prev => prev ? {
                      ...prev,
                      general: { ...prev.general, timezone: e.target.value }
                    } : null)}
                  >
                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                    <MenuItem value="UTC">UTC</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.general.maintenanceMode}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          general: { ...prev.general, maintenanceMode: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Maintenance Mode"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.general.debugMode}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          general: { ...prev.general, debugMode: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Debug Mode"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={activeTab} index={1}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Session Timeout (minutes)"
                  type="number"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    security: { ...prev.security, sessionTimeout: parseInt(e.target.value) }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Login Attempts"
                  type="number"
                  value={settings.security.maxLoginAttempts}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    security: { ...prev.security, maxLoginAttempts: parseInt(e.target.value) }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password Minimum Length"
                  type="number"
                  value={settings.security.passwordMinLength}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    security: { ...prev.security, passwordMinLength: parseInt(e.target.value) }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.security.requireMFA}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          security: { ...prev.security, requireMFA: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Require Multi-Factor Authentication"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.security.sslEnforced}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          security: { ...prev.security, sslEnforced: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enforce SSL/HTTPS"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Allowed IP Addresses
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {settings.security.allowedIPs.map((ip, index) => (
                    <Chip
                      key={index}
                      label={ip}
                      onDelete={() => {
                        const newIPs = settings.security.allowedIPs.filter((_, i) => i !== index);
                        setSettings(prev => prev ? {
                          ...prev,
                          security: { ...prev.security, allowedIPs: newIPs }
                        } : null);
                      }}
                    />
                  ))}
                </Box>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const newIP = prompt('Enter IP address or CIDR block:');
                    if (newIP) {
                      setSettings(prev => prev ? {
                        ...prev,
                        security: { ...prev.security, allowedIPs: [...prev.security.allowedIPs, newIP] }
                      } : null);
                    }
                  }}
                >
                  Add IP Address
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* AWS Integration */}
        <TabPanel value={activeTab} index={2}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>AWS Region</InputLabel>
                  <Select
                    value={settings.aws.region}
                    label="AWS Region"
                    onChange={(e) => setSettings(prev => prev ? {
                      ...prev,
                      aws: { ...prev.aws, region: e.target.value }
                    } : null)}
                  >
                    <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                    <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                    <MenuItem value="eu-west-1">Europe (Ireland)</MenuItem>
                    <MenuItem value="ap-southeast-1">Asia Pacific (Singapore)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="S3 Bucket Name"
                  value={settings.aws.bucketName}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    aws: { ...prev.aws, bucketName: e.target.value }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Access Key ID"
                  value={settings.aws.accessKeyId}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setEditingKey('accessKey')}>
                        <EditIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Backup Retention (days)"
                  type="number"
                  value={settings.aws.backupRetentionDays}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    aws: { ...prev.aws, backupRetentionDays: parseInt(e.target.value) }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.aws.cloudWatchEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          aws: { ...prev.aws, cloudWatchEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable CloudWatch Monitoring"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.aws.costAlertsEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          aws: { ...prev.aws, costAlertsEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable Cost Alerts"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={handleTestConnection}
                  startIcon={<CloudIcon />}
                >
                  Test AWS Connection
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Notifications */}
        <TabPanel value={activeTab} index={3}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.emailEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          notifications: { ...prev.notifications, emailEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Email Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.smsEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          notifications: { ...prev.notifications, smsEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="SMS Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.slackEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          notifications: { ...prev.notifications, slackEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Slack Integration"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={settings.notifications.webhookUrl}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    notifications: { ...prev.notifications, webhookUrl: e.target.value }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Alert Thresholds
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Error Rate (%)"
                      type="number"
                      value={settings.notifications.alertThresholds.errorRate}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          alertThresholds: {
                            ...prev.notifications.alertThresholds,
                            errorRate: parseFloat(e.target.value)
                          }
                        }
                      } : null)}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Response Time (ms)"
                      type="number"
                      value={settings.notifications.alertThresholds.responseTime}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          alertThresholds: {
                            ...prev.notifications.alertThresholds,
                            responseTime: parseInt(e.target.value)
                          }
                        }
                      } : null)}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Disk Usage (%)"
                      type="number"
                      value={settings.notifications.alertThresholds.diskUsage}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          alertThresholds: {
                            ...prev.notifications.alertThresholds,
                            diskUsage: parseInt(e.target.value)
                          }
                        }
                      } : null)}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Memory Usage (%)"
                      type="number"
                      value={settings.notifications.alertThresholds.memoryUsage}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          alertThresholds: {
                            ...prev.notifications.alertThresholds,
                            memoryUsage: parseInt(e.target.value)
                          }
                        }
                      } : null)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Performance */}
        <TabPanel value={activeTab} index={4}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.performance.cacheEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          performance: { ...prev.performance, cacheEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable Caching"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.performance.rateLimitEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          performance: { ...prev.performance, rateLimitEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable Rate Limiting"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.performance.compressionEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          performance: { ...prev.performance, compressionEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable Compression"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.performance.cdnEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          performance: { ...prev.performance, cdnEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable CDN"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cache TTL (seconds)"
                  type="number"
                  value={settings.performance.cacheTTL}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    performance: { ...prev.performance, cacheTTL: parseInt(e.target.value) }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rate Limit Window (seconds)"
                  type="number"
                  value={settings.performance.rateLimitWindow}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    performance: { ...prev.performance, rateLimitWindow: parseInt(e.target.value) }
                  } : null)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        {/* Backup */}
        <TabPanel value={activeTab} index={5}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.backup.autoBackupEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          backup: { ...prev.backup, autoBackupEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable Automatic Backups"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.backup.s3BackupEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          backup: { ...prev.backup, s3BackupEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable S3 Backup Storage"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.backup.encryptionEnabled}
                        onChange={(e) => setSettings(prev => prev ? {
                          ...prev,
                          backup: { ...prev.backup, encryptionEnabled: e.target.checked }
                        } : null)}
                      />
                    }
                    label="Enable Backup Encryption"
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Backup Frequency</InputLabel>
                  <Select
                    value={settings.backup.backupFrequency}
                    label="Backup Frequency"
                    onChange={(e) => setSettings(prev => prev ? {
                      ...prev,
                      backup: { ...prev.backup, backupFrequency: e.target.value }
                    } : null)}
                  >
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Retention Period (days)"
                  type="number"
                  value={settings.backup.retentionPeriod}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    backup: { ...prev.backup, retentionPeriod: parseInt(e.target.value) }
                  } : null)}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<BackupIcon />}
                  onClick={() => dispatch(addNotification({
                    type: 'info',
                    message: 'Manual backup initiated',
                  }))}
                >
                  Run Manual Backup
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>
      </Card>

      {/* Connection Test Dialog */}
      <Dialog open={testConnectionDialog} onClose={() => setTestConnectionDialog(false)}>
        <DialogTitle>Testing AWS Connection</DialogTitle>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
          <CircularProgress size={24} />
          <Typography>Verifying AWS credentials and permissions...</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Settings;